import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Activity, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import RebalanceNodeDiff, { hasNodeObjects } from './RebalanceNodeDiff';

import { parseUtc } from '../../lib/parseUtc';
/**
 * InClusterOptimizerPanel — P1 DISPLAY ONLY.
 *
 * Read-only view of the in-cluster optimizer (RebalancePlans + migrations)
 * reported by the backend under `in_cluster_optimizer` on
 * GET /v1/clusters/{id}/node-optimization/status. The parent page fetches
 * /status and passes the block down as `data`; this component owns NO
 * axios call and NO control/toggle/handler — purely surfaces state.
 *
 * Contract (shape of `data`):
 *   { installed, active_plans,
 *     plans: [ {name, phase, nodes_to_remove, projected_monthly_usd,
 *               failure_phase, created_at,
 *               nodes_to_remove_detail?: [{node_name, instance_type,
 *                 capacity_type, status, reason}],   // agent 1.0.25+, omitempty
 *               nodes_to_create?: [{instance_type, capacity_type, status}]} ],
 *     migrations: {total, succeeded, failed, success_rate},
 *     consolidation_events_24h, realized_savings_monthly_usd }
 */

// Phase → badge colour: emerald for done, amber for pending/partial,
// red for failed, neutral slate for in-flight/unknown.
const phaseBadge = (phase) => {
  switch (phase) {
    case 'Pending':
      return <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">Pending</Badge>;
    case 'Ready':
      return <Badge className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">Ready</Badge>;
    case 'Executing':
      return <Badge className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">Executing</Badge>;
    case 'Completed':
      return <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">Completed</Badge>;
    case 'Partial':
      return <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">Partial</Badge>;
    case 'Failed':
      return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">Failed</Badge>;
    default:
      return <Badge className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">{phase || 'Unknown'}</Badge>;
  }
};

// Human labels for internal failure-phase codes; the raw code survives only
// in the badge's title= tooltip.
const FAILURE_PHASES = {
  drain_admission_recheck_failed: 'Drain blocked',
  Creation: 'Node creation failed',
  Deletion: 'Node removal failed',
};
const failureLabel = (code) => FAILURE_PHASES[code] || code.replace(/[-_]/g, ' ');

// Monthly dollars: whole dollars with thousands separators — no cents noise.
const fmtMo = (v) => '$' + Math.round(v).toLocaleString() + '/mo';

const InClusterOptimizerPanel = ({ data, clusterId }) => {
  // Which plan's node-by-node diff is expanded — keyed by plan name so a
  // refreshed plans list keeps the operator's expansion. null = all collapsed.
  const [expandedPlan, setExpandedPlan] = useState(null);
  // N1 Savings Trust Ledger: realized $/mo = the engine's price-VALIDATED
  // savings summed over Completed/Partial plans (30d window) — replaces the
  // dead action-queue metric that read $0.00 forever under engine-SoR.
  const [ledger, setLedger] = useState(null);
  useEffect(() => {
    if (!clusterId) return;
    let live = true;
    const load = () =>
      axios.get(`${API}/v1/clusters/${clusterId}/node-optimization/engine-savings-summary`)
        .then((r) => { if (live) setLedger(r.data); })
        .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { live = false; clearInterval(t); };
  }, [clusterId]);

  // Defensive: page only renders us when installed===true, but guard anyway.
  if (!data) return null;

  const plans = Array.isArray(data.plans) ? data.plans : [];
  const migrations = data.migrations || {};
  const migrationsTotal = migrations.total || 0;
  // success_rate arrives as a 0..1 fraction; render as a whole percent.
  const successRatePct = (migrations.success_rate || 0) * 100;
  const realizedSavings = ledger?.realized_monthly_total ?? (data.realized_savings_monthly_usd || 0);

  // Newest first — sort by created_at descending. Rows without a timestamp
  // sort to the bottom (empty string compares low) rather than crashing.
  const sortedPlans = [...plans].sort((a, b) =>
    (b.created_at || '').localeCompare(a.created_at || '')
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          In-Cluster Optimizer
          {data.installed && (
            <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 flex items-center gap-1">
              <Activity className="h-3 w-3" />
              Live
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Summary stats. Migration success rate hides when there are no
            migrations yet so we don't show a misleading "0%". */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400">Active plans</p>
            <p className="text-lg font-semibold tabular-nums">{data.active_plans || 0}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400">Consolidations (24h)</p>
            <p className="text-lg font-semibold tabular-nums">{data.consolidation_events_24h || 0}</p>
          </div>
          {migrationsTotal > 0 && (
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400">Migration success</p>
              <p className="text-lg font-semibold tabular-nums">{successRatePct.toFixed(0)}%</p>
            </div>
          )}
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400">Realized savings</p>
            <p className="text-lg font-semibold tabular-nums">
              ${Math.round(realizedSavings).toLocaleString()}
              <span className="text-xs text-slate-400 font-normal">/mo</span>
            </p>
          </div>
        </div>

        {/* RebalancePlan timeline — newest first. */}
        <div className="mt-4 pt-4 border-t">
          {sortedPlans.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500 dark:text-slate-400">
              No rebalance activity right now. Your cluster is running at its optimized size.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Plan</th>
                    <th className="text-left py-2 px-2 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Status</th>
                    <th className="text-right py-2 px-2 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Nodes removed</th>
                    <th className="text-right py-2 px-2 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Est. savings</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPlans.map((plan, index) => {
                    // Node-by-node diff (agent 1.0.25+, omitempty): remove
                    // side under nodes_to_remove_detail, create side under
                    // nodes_to_create — only when they carry objects (the
                    // legacy nodes_to_remove int count is untouched).
                    const removeDetail = hasNodeObjects(plan.nodes_to_remove_detail)
                      ? plan.nodes_to_remove_detail
                      : [];
                    const createDetail = hasNodeObjects(plan.nodes_to_create)
                      ? plan.nodes_to_create
                      : [];
                    const hasDiff = removeDetail.length > 0 || createDetail.length > 0;
                    const planKey = plan.name || String(index);
                    const expanded = hasDiff && expandedPlan === planKey;
                    return (
                    <React.Fragment key={planKey}>
                    <tr className="border-b hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-1">
                          {hasDiff && (
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedPlan(expanded ? null : planKey)
                              }
                              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                              title="Show node-by-node changes"
                            >
                              {expanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                          )}
                          <p className="font-medium">
                            {(plan.nodes_to_remove || 0) > 0
                              ? `Remove ${plan.nodes_to_remove} node${plan.nodes_to_remove === 1 ? '' : 's'}`
                              : 'Rebalance plan'}
                          </p>
                        </div>
                        {(plan.name || plan.created_at) && (
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {plan.name && (
                              <span className="font-mono" title={plan.name}>{plan.name}</span>
                            )}
                            {plan.name && plan.created_at && ' · '}
                            {plan.created_at && parseUtc(plan.created_at).toLocaleString()}
                          </p>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          {phaseBadge(plan.phase)}
                          {plan.failure_phase && plan.phase !== 'Completed' && (
                            <Badge className="bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 flex items-center gap-1" title={plan.failure_message || `Engine phase: ${plan.failure_phase}`}>
                              <AlertTriangle className="h-3 w-3" />
                              {failureLabel(plan.failure_phase)}
                            </Badge>
                          )}
                        </div>
                        {/* The engine's WHY (status.message, agent 1.0.26+ / engine l2pin5+):
                            a red plan is diagnosable from the UI alone — no log spelunking.
                            Honest-empty: absent on older engines, renders nothing. */}
                        {plan.failure_message && plan.phase === 'Failed' && (
                          <p className="mt-1 text-[11px] leading-snug text-red-700/80 dark:text-red-400/80 max-w-[28rem] truncate" title={plan.failure_message}>
                            {plan.failure_message}
                          </p>
                        )}
                      </td>
                      <td className="py-3 px-2 text-right font-medium tabular-nums">
                        {plan.nodes_to_remove || 0}
                      </td>
                      <td className="py-3 px-2 text-right font-medium tabular-nums text-emerald-700 dark:text-emerald-400">
                        {fmtMo(plan.projected_monthly_usd || 0)}
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="border-b bg-slate-50 dark:bg-slate-800/50">
                        <td colSpan={4} className="py-3 px-4">
                          <RebalanceNodeDiff
                            removeNodes={removeDetail}
                            createNodes={createDetail}
                            classification={data?.node_classification}
                          />
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default InClusterOptimizerPanel;
